/**
 * Add Signal Modal
 * Form to update entity signals (current values, scores, statuses)
 */

import React, { useState } from 'react';
import useStore from '../store/useStore';
import './AddSignalModal.css';

const AddSignalModal = ({ onClose }) => {
  const { entities, createSignal } = useStore();
  const [formData, setFormData] = useState({
    EntityId: '',
    SignalType: 'Value',
    NumericValue: '',
    Status: 'On Track',
    BooleanValue: false,
    TextValue: '',
    Reason: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.EntityId) {
      newErrors.EntityId = 'Please select an entity';
    }
    
    if (formData.SignalType === 'Value' || formData.SignalType === 'Score') {
      if (!formData.NumericValue && formData.NumericValue !== 0) {
        newErrors.NumericValue = 'Numeric value is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }

  setIsSubmitting(true);

  try {
    const signalData = {
      EntityIdLookupId: parseInt(formData.EntityId),
      SignalType: formData.SignalType,
      Timestamp: new Date().toISOString(),
    };

    // Add type-specific fields
    if (formData.SignalType === 'Value' || formData.SignalType === 'Score') {
      signalData.NumericValue = parseFloat(formData.NumericValue);
    }

    if (formData.SignalType === 'Status') {
      signalData.Status = formData.Status;
    }

    if (formData.SignalType === 'Boolean') {
      signalData.BooleanValue = formData.BooleanValue === 'true';
    }

    if (formData.SignalType === 'Text') {
      signalData.TextValue = formData.TextValue;
    }

    if (formData.Reason) {
      signalData.Reason = formData.Reason;
    }

    // Check if updating existing signal
    const sharePointService = (await import('../services/sharePointService')).default;
    const existingSignal = await sharePointService.checkDuplicateSignal(
      signalData.EntityIdLookupId,
      signalData.SignalType,
      signalData.Timestamp
    );

    await createSignal(signalData);

    // Show appropriate message
    if (existingSignal) {
      alert('Signal updated successfully!');
    } else {
      alert('Signal created successfully!');
    }

    onClose();
  } catch (error) {
    console.error('Error creating/updating signal:', error);
    alert('Failed to save signal. Please try again.');
  } finally {
    setIsSubmitting(false);
  }
};

  const selectedEntity = entities.find(e => e.Id === parseInt(formData.EntityId));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Update Entity Signal</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="signal-form">
          <div className="form-group">
            <label htmlFor="EntityId">Select Entity *</label>
            <select
              id="EntityId"
              name="EntityId"
              value={formData.EntityId}
              onChange={handleChange}
              className={errors.EntityId ? 'error' : ''}
              disabled={isSubmitting}
            >
              <option value="">-- Select an entity --</option>
              {entities.map(entity => (
                <option key={entity.Id} value={entity.Id}>
                  [{entity.EntityType}] {entity.Title}
                </option>
              ))}
            </select>
            {errors.EntityId && <span className="error-message">{errors.EntityId}</span>}
          </div>

          {selectedEntity && (
            <div className="entity-info">
              <div className="info-item">
                <strong>Type:</strong> {selectedEntity.EntityType}
              </div>
              {selectedEntity.TargetValue && (
                <div className="info-item">
                  <strong>Target:</strong> {selectedEntity.TargetValue} {selectedEntity.TargetUnit}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="SignalType">Signal Type *</label>
            <select
              id="SignalType"
              name="SignalType"
              value={formData.SignalType}
              onChange={handleChange}
              disabled={isSubmitting}
            >
              <option value="Value">Value (Numeric)</option>
              <option value="Score">Score (0-1)</option>
              <option value="Status">Status</option>
              <option value="Boolean">Boolean (Yes/No)</option>
              <option value="Text">Text</option>
            </select>
          </div>

          {/* Numeric Value Field */}
          {(formData.SignalType === 'Value' || formData.SignalType === 'Score') && (
            <div className="form-group">
              <label htmlFor="NumericValue">
                {formData.SignalType === 'Score' ? 'Score (0-1) *' : 'Numeric Value *'}
              </label>
              <input
                type="number"
                id="NumericValue"
                name="NumericValue"
                value={formData.NumericValue}
                onChange={handleChange}
                step={formData.SignalType === 'Score' ? '0.01' : '0.01'}
                min={formData.SignalType === 'Score' ? '0' : undefined}
                max={formData.SignalType === 'Score' ? '1' : undefined}
                placeholder={formData.SignalType === 'Score' ? 'e.g., 0.85' : 'e.g., 5200'}
                className={errors.NumericValue ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.NumericValue && <span className="error-message">{errors.NumericValue}</span>}
              {formData.SignalType === 'Score' && (
                <small className="help-text">Enter as decimal: 0 = 0%, 0.5 = 50%, 1 = 100%</small>
              )}
            </div>
          )}

          {/* Status Field */}
          {(formData.SignalType === 'Status' || formData.SignalType === 'Value' || formData.SignalType === 'Score') && (
            <div className="form-group">
              <label htmlFor="Status">Status</label>
              <select
                id="Status"
                name="Status"
                value={formData.Status}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="Met">Met</option>
                <option value="On Track">On Track</option>
                <option value="At Risk">At Risk</option>
                <option value="Delayed">Delayed</option>
                <option value="Blocked">Blocked</option>
                <option value="Not Met">Not Met</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
          )}

          {/* Boolean Field */}
          {formData.SignalType === 'Boolean' && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="BooleanValue"
                  checked={formData.BooleanValue}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
                <span>Condition is met / True</span>
              </label>
            </div>
          )}

          {/* Text Field */}
          {formData.SignalType === 'Text' && (
            <div className="form-group">
              <label htmlFor="TextValue">Text Value</label>
              <textarea
                id="TextValue"
                name="TextValue"
                value={formData.TextValue}
                onChange={handleChange}
                rows="3"
                placeholder="Enter text information"
                disabled={isSubmitting}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="Reason">Reason (Optional)</label>
            <textarea
              id="Reason"
              name="Reason"
              value={formData.Reason}
              onChange={handleChange}
              rows="3"
              placeholder="e.g., Pipeline shutdown for maintenance, weather downtime, equipment failure..."
              disabled={isSubmitting}
            />
            <small className="help-text">Explain reason for non-performance. Shown in alerts and email notifications.</small>
          </div>

          <div className="form-info">
            <strong>Note:</strong> This will create a new signal with current timestamp.
            Only the latest signal per entity is used in evaluations.
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Signal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSignalModal;